import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_IMAGES } from "../mock-data";

gsap.registerPlugin(ScrollTrigger);

export function EditorialLayout({ formSlot, program, title, dateTime, location, description, slug }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);

  const dateFormatted = dateTime
    ? new Date(dateTime).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).replace(/\./g, " / ")
    : "-- / -- / ----";
  const [firstName, lastName] = title.split("&").map((s) => s.trim());

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.2, easing: (t) => 1 - Math.pow(1 - t, 4) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      // Hero big text slide up
      gsap.from(".ed-hero-line", {
        y: 120,
        opacity: 0,
        stagger: 0.12,
        duration: 1,
        ease: "power4.out",
      });

      // Clip-path wipe for split screen
      gsap.from(".ed-split-image", {
        clipPath: "inset(0 100% 0 0)",
        duration: 1.4,
        ease: "power3.inOut",
        scrollTrigger: { trigger: ".ed-split-section", start: "top 70%", toggleActions: "play reverse play reverse" },
      });

      gsap.from(".ed-split-text", {
        x: 60,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: { trigger: ".ed-split-section", start: "top 60%", toggleActions: "play reverse play reverse" },
      });

      // Chapter diagonal wipe
      gsap.from(".ed-chapter-bg", {
        clipPath: "polygon(0 0, 0 0, 0 100%, 0 100%)",
        duration: 1.2,
        ease: "power3.inOut",
        scrollTrigger: { trigger: ".ed-chapter-section", start: "top 70%", toggleActions: "play reverse play reverse" },
      });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
    };
  }, []);

  return (
    <main className="bg-black text-white overflow-x-hidden">
      {/* ── Hero: Big bold type ───────────────────────────────────────── */}
      <section className="min-h-screen flex flex-col items-center justify-center px-4 sm:px-12 overflow-hidden">
        <div className="overflow-hidden">
          <p className="ed-hero-line text-xs tracking-[0.5em] uppercase text-white/30 mb-6">
            {dateFormatted}
          </p>
        </div>
        <div className="overflow-hidden">
          <h1 className="ed-hero-line font-black uppercase text-[clamp(3.5rem,12vw,10rem)] leading-[0.85] tracking-tighter text-white">
            {firstName ?? title}
          </h1>
        </div>
        {lastName && (
          <div className="overflow-hidden w-full">
            <h1 className="ed-hero-line font-black uppercase text-[clamp(3.5rem,12vw,10rem)] leading-[0.85] tracking-tighter text-white/20 text-right">
              {lastName}
            </h1>
          </div>
        )}
        <div className="overflow-hidden mt-8">
          <p className="ed-hero-line text-xs tracking-[0.5em] uppercase text-white/30">
            {location ?? ""}
          </p>
        </div>
      </section>

      {/* ── Marquee ───────────────────────────────────────────────────── */}
      <div className="overflow-hidden bg-white text-black py-4 border-y border-neutral-800">
        <div
          className="whitespace-nowrap text-sm font-black uppercase tracking-widest"
          style={{ animation: "marquee 18s linear infinite" }}
        >
          {Array(4).fill(null).map((_, i) => (
            <span key={i} className="mx-8">
              {title.toUpperCase()} · {dateFormatted} · {(location ?? "").toUpperCase()} · ❤
            </span>
          ))}
        </div>
      </div>
      <style>{`@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>

      {/* ── Split Screen ─────────────────────────────────────────────── */}
      <section className="ed-split-section min-h-screen grid md:grid-cols-2 overflow-hidden">
        <div
          className="ed-split-image h-64 md:h-auto bg-neutral-800"
          style={{ clipPath: "inset(0 0% 0 0)" }}
        >
          <img src={DEMO_IMAGES.gallery[1]} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="ed-split-text flex items-center bg-black px-8 sm:px-16 py-16">
          <div>
            <p className="text-xs tracking-[0.4em] uppercase text-white/30 mb-6">Hikayemiz</p>
            <p className="text-2xl sm:text-3xl font-serif font-light text-white leading-relaxed">
              {description ?? "İki kalbin buluştuğu gün başladı her şey."}
            </p>
          </div>
        </div>
      </section>

      {/* ── Chapter: diagonal wipe ───────────────────────────────────── */}
      <section className="ed-chapter-section relative min-h-[70vh] flex items-center overflow-hidden bg-black py-24">
        <div
          className="ed-chapter-bg absolute inset-0 bg-white"
          style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)" }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-8 grid sm:grid-cols-2 gap-8 w-full">
          <div className="border border-black/10 p-8">
            <p className="text-xs tracking-widest uppercase text-black/40 mb-4">Tarih</p>
            <p className="text-3xl font-black text-black">{dateFormatted}</p>
          </div>
          <div className="border border-black/10 p-8">
            <p className="text-xs tracking-widest uppercase text-black/40 mb-4">Mekan</p>
            <p className="text-3xl font-black text-black leading-tight">
              {location ?? "Açıklanacak"}
            </p>
          </div>
        </div>
      </section>

      {/* ── Gallery grid ─────────────────────────────────────────────── */}
      <section className="grid grid-cols-3 gap-1 bg-black">
        {DEMO_IMAGES.gallery.map((src, i) => (
          <div key={i} className="aspect-square overflow-hidden">
            <img src={src} alt="" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700 grayscale hover:grayscale-0" />
          </div>
        ))}
      </section>

      {/* ── Form ─────────────────────────────────────────────────────── */}
      <section className="min-h-screen flex items-center justify-center bg-black border-t border-white/10 px-6 py-24">
        <div className="w-full max-w-md">
          <p className="text-center text-xs tracking-[0.4em] uppercase text-white/30 mb-4">Katılım</p>
          <h2 className="text-center text-3xl font-black uppercase tracking-tighter text-white mb-10">
            Bize Katıl
          </h2>
          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
        </div>
      </section>
    </main>
  );
}
